// Snowfall easter egg — a tasteful full-screen canvas overlay (pointer-events:none, so the UI stays
// fully usable). Two moods, same effect: a cinematic "winter is coming" (cool vignette + a fading
// title, auto-melts after ~30s) and a plain "snow" toggle that gets heavier each time up to a capped
// blizzard. Self-cleaning, like matrixRain.ts. No dependencies.
const Z = 9998;

type Level = 1 | 2 | 3;
interface LevelCfg { flakes: number; speed: number; wind: number; }
const LEVELS: Record<Level, LevelCfg> = {
    1: { flakes: 70, speed: 1.0, wind: 0.3 },
    2: { flakes: 150, speed: 1.5, wind: 0.6 },
    3: { flakes: 280, speed: 2.3, wind: 1.0 }, // blizzard — capped so the page stays usable
};

// "snow" stays hidden until the user first discovers it via "winter is coming".
const SNOW_KEY = 'god_mode_snow';
export const isSnowDiscovered = (): boolean => {
    try { return localStorage.getItem(SNOW_KEY) === '1'; } catch { return false; }
};
export const markSnowDiscovered = (): void => {
    try { localStorage.setItem(SNOW_KEY, '1'); } catch { /* ignore */ }
};

interface Flake { x: number; y: number; r: number; vy: number; drift: number; phase: number; }

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let cold: HTMLDivElement | null = null;
let flakes: Flake[] = [];
let level: Level = 1;
let targetFlakes = 0;
let accum = 0;
let raf = 0;
let W = 0;
let H = 0;
let dpr = 1;
let melting = false;
let autoMelt = 0;
let teardownTimer = 0; // the pending post-melt teardown; cancelled if snow restarts mid-melt
let rampTimers: number[] = [];
let bankPhase = 0;
let dramatic = false;

// The first time a dramatic "winter is coming" passes, drop one subtle clue toward the fire egg.
const FIRE_HINT_KEY = 'god_mode_fire_hint';

const rnd = (a: number, b: number) => a + Math.random() * (b - a);

// Esc always stops the snow — the easy-to-discover way out (typed stop-words still work too).
const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') stopSnow();
};

const makeFlake = (): Flake => ({
    x: Math.random() * W,
    y: Math.random() * H,
    r: rnd(1.2, 3.6),
    vy: rnd(0.6, 1.4),
    drift: rnd(-0.5, 0.5),
    phase: Math.random() * Math.PI * 2,
});

const resize = () => {
    if (!canvas || !ctx) return;
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
};

const setLevel = (n: Level) => {
    level = n;
    targetFlakes = LEVELS[n].flakes;
};

const loop = () => {
    raf = requestAnimationFrame(loop);
    if (!ctx) return;
    const cfg = LEVELS[level];

    // Ease the flake count toward the target so heavier/lighter transitions are gradual.
    if (flakes.length < targetFlakes) {
        for (let i = 0; i < 4 && flakes.length < targetFlakes; i++) flakes.push(makeFlake());
    } else if (flakes.length > targetFlakes) {
        flakes.splice(0, Math.min(3, flakes.length - targetFlakes));
    }

    ctx.clearRect(0, 0, W, H);

    // A snow bank builds along the bottom while active; shrinks away while melting.
    const maxAccum = melting ? 0 : 18 + level * 22; // ~40 / 62 / 84 px by level
    accum += (maxAccum - accum) * 0.02;
    bankPhase += 0.01;

    for (const f of flakes) {
        f.phase += 0.01;
        f.y += f.vy * cfg.speed * (melting ? 1.7 : 1);
        f.x += (f.drift + Math.sin(f.phase) * 0.5) * cfg.wind;
        if (f.y > H + 6) { f.y = -6; f.x = Math.random() * W; }
        if (f.x < -6) f.x = W + 6;
        else if (f.x > W + 6) f.x = -6;
        ctx.globalAlpha = melting ? 0.6 : 0.9;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.globalAlpha = 1;

    if (accum > 1) {
        const base = H - accum;
        ctx.beginPath();
        ctx.moveTo(0, H);
        ctx.lineTo(0, base);
        for (let x = 0; x <= W; x += 24) {
            const y = base + Math.sin(x * 0.012 + bankPhase) * 4 + Math.sin(x * 0.05) * 2;
            ctx.lineTo(x, y);
        }
        ctx.lineTo(W, H);
        ctx.closePath();
        const g = ctx.createLinearGradient(0, base - 6, 0, H);
        g.addColorStop(0, 'rgba(255,255,255,0.75)');
        g.addColorStop(0.15, 'rgba(255,255,255,0.96)');
        g.addColorStop(1, 'rgba(240,246,255,0.98)');
        ctx.fillStyle = g;
        ctx.fill();
    }
};

// The cinematic "winter is coming" extras: a cool vignette and a fading title.
const addCold = () => {
    if (cold) return;
    cold = document.createElement('div');
    cold.style.cssText =
        `position:fixed;inset:0;z-index:${Z - 1};pointer-events:none;opacity:0;transition:opacity 1200ms ease;` +
        'background:radial-gradient(ellipse at center, rgba(20,40,80,0) 45%, rgba(8,18,45,0.40) 100%), rgba(120,150,200,0.10);';
    document.body.appendChild(cold);
    requestAnimationFrame(() => { if (cold) cold.style.opacity = '1'; });
};

const showWinterTitle = () => {
    const el = document.createElement('div');
    el.style.cssText =
        `position:fixed;inset:0;z-index:${Z + 1};pointer-events:none;opacity:0;transition:opacity 1200ms ease;` +
        'display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;' +
        'text-shadow:0 2px 24px rgba(0,0,0,0.9);' +
        // Dark halo behind the text so it stays legible on light pages too.
        'background:radial-gradient(ellipse 620px 320px at center, rgba(5,12,30,0.88) 0%, rgba(5,12,30,0.55) 42%, rgba(5,12,30,0) 75%);';
    el.innerHTML =
        '<div style="font:700 44px system-ui,sans-serif;letter-spacing:6px;color:#eaf2ff;">❄️ WINTER IS HERE</div>' +
        '<div style="margin-top:10px;font:400 20px system-ui,sans-serif;color:#b9cdea;">You were warned.</div>';
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    window.setTimeout(() => { el.style.opacity = '0'; }, 4200);
    window.setTimeout(() => el.remove(), 5600);
};

// Shown once ever, as a dramatic winter fades: the first, oblique hint toward "dracarys".
const showFireHint = () => {
    let seen = false;
    try { seen = localStorage.getItem(FIRE_HINT_KEY) === '1'; } catch { /* ignore */ }
    if (seen) return;
    try { localStorage.setItem(FIRE_HINT_KEY, '1'); } catch { /* ignore */ }
    const el = document.createElement('div');
    el.style.cssText =
        `position:fixed;inset:0;z-index:${Z + 1};pointer-events:none;opacity:0;transition:opacity 1400ms ease;` +
        'display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;';
    el.innerHTML =
        '<div style="font:600 26px system-ui,sans-serif;letter-spacing:2px;color:#dce7ff;text-shadow:0 2px 20px rgba(0,0,0,0.9);">❄️ Winter has passed.</div>' +
        '<div style="margin-top:8px;font:400 17px system-ui,sans-serif;color:#9fb2d4;text-shadow:0 2px 20px rgba(0,0,0,0.9);">Some say only fire can defeat it…</div>';
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    window.setTimeout(() => { el.style.opacity = '0'; }, 3600);
    window.setTimeout(() => el.remove(), 5200);
};

const teardown = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    window.removeEventListener('resize', resize);
    window.removeEventListener('keydown', onKey);
    canvas?.remove();
    canvas = null;
    ctx = null;
    cold?.remove();
    cold = null;
    flakes = [];
    accum = 0;
    level = 1;
    melting = false;
    teardownTimer = 0;
    if (dramatic) {
        dramatic = false;
        showFireHint();
    }
};

export const isSnowing = (): boolean => !!canvas && !melting;
export const snowLevel = (): number => level;

export function startSnow(opts?: { dramatic?: boolean }): void {
    if (typeof document === 'undefined') return;
    // Respect reduced-motion: skip the continuous snowfall entirely (as matrixRain does). The other
    // snow functions all no-op without a canvas, so nothing downstream breaks.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    if (!canvas) {
        dpr = window.devicePixelRatio || 1;
        canvas = document.createElement('canvas');
        canvas.style.cssText = `position:fixed;inset:0;z-index:${Z};pointer-events:none;transition:opacity 800ms ease;opacity:1;`;
        document.body.appendChild(canvas);
        ctx = canvas.getContext('2d');
        if (!ctx) {
            canvas.remove();
            canvas = null;
            return;
        }
        resize();
        window.addEventListener('resize', resize);
        window.addEventListener('keydown', onKey);
        flakes = [];
        setLevel(1);
        raf = requestAnimationFrame(loop);
    }
    // Restarting mid-melt: cancel the pending teardown and make the fading canvas fully visible again,
    // otherwise the scheduled teardown would remove the just-restarted snow (and it'd be invisible).
    if (teardownTimer) {
        clearTimeout(teardownTimer);
        teardownTimer = 0;
    }
    melting = false;
    if (canvas) canvas.style.opacity = '1';
    if (cold) cold.style.opacity = '1';

    if (opts?.dramatic) {
        dramatic = true;
        addCold();
        showWinterTitle();
        rampTimers.push(window.setTimeout(() => setLevel(2), 4000)); // gradually heavier
        clearTimeout(autoMelt);
        autoMelt = window.setTimeout(() => stopSnow(), 30_000); // ...then melts on its own
    }
}

// Bump the snowfall one level heavier (capped at blizzard). Cancels any auto-melt: manual takes over.
export function snowHeavier(): number {
    if (!canvas) {
        startSnow();
        return level;
    }
    clearTimeout(autoMelt);
    setLevel(Math.min(3, level + 1) as Level);
    return level;
}

export function stopSnow(): void {
    if (!canvas || melting) return;
    melting = true;
    clearTimeout(autoMelt);
    rampTimers.forEach((t) => clearTimeout(t));
    rampTimers = [];
    canvas.style.opacity = '0';
    if (cold) cold.style.opacity = '0';
    teardownTimer = window.setTimeout(teardown, 900);
}
